import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { wipeAllScanDrafts } from '@/lib/offlineQueue';
import { captureAffFromUrl, getAff } from '@/lib/landingVariants';
import i18n from '@/lib/i18n';
import { LANGUAGES } from '@/lib/i18n/registry';

const logSecurityEvent = async (userId: string | undefined, eventType: string, metadata?: any) => {
  if (!userId) return;
  try {
    await supabase.rpc('log_security_event', {
      p_user_id: userId,
      p_event_type: eventType,
      p_metadata: metadata || null
    });
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any; data?: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Capture ?aff=<code> once per session so influencer attribution
    // survives across signup → later upgrade, even on a different device.
    captureAffFromUrl();

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Silently try to link any orphan Lemon Squeezy purchase that was
        // made directly on fennest.lemonsqueezy.com with this user's email
        // (e.g. before the account existed, or different signup flow).
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            supabase.functions
              .invoke('claim-lemonsqueezy-purchase', { body: { silent: true } })
              .catch(() => { /* best-effort, no UI */ });
          }, 0);

          // First-touch write of the affiliate code onto the user's profile.
          // Only sets it if the column is currently null - never overwrites.
          const aff = getAff();
          if (aff) {
            const userId = session.user.id;
            setTimeout(async () => {
              try {
                const { data: prof } = await supabase
                  .from('profiles')
                  .select('affiliate_code')
                  .eq('user_id', userId)
                  .maybeSingle();
                if (prof && !prof.affiliate_code) {
                  const { error: updateErr } = await supabase
                    .from('profiles')
                    .update({ affiliate_code: aff })
                    .eq('user_id', userId)
                    .is('affiliate_code', null);
                  // Only log the signup attribution when the first-touch write
                  // actually happened, so re-signins never spam the audit table.
                  if (!updateErr) {
                    await supabase
                      .from('affiliate_attributions')
                      .insert({
                        user_id: userId,
                        affiliate_code: aff,
                        source: 'signup',
                      } as any);
                  }
                }
              } catch {
                // best-effort
              }
            }, 0);
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Log security event (non-blocking)
    if (!error) {
      const { data: { user: loggedInUser } } = await supabase.auth.getUser();
      logSecurityEvent(loggedInUser?.id, 'successful_login', { email });
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    // Hand the client-resolved, validated language to the backend so the
    // first verification email and the new profile use it.
    const preferred_language = i18n.language in LANGUAGES ? i18n.language : 'en';
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { preferred_language },
      }
    });
    
    // Log security event (non-blocking)
    if (!error) {
      const { data: { user: newUser } } = await supabase.auth.getUser();
      logSecurityEvent(newUser?.id, 'successful_signup', { email });
    }
    
    return { error, data };
  };

  const signOut = async () => {
    const currentUserId = user?.id;
    // Clear any cached scan drafts (incl. on-device receipt images) so the
    // next account on a shared device sees nothing.
    await wipeAllScanDrafts().catch(() => {});
    await supabase.auth.signOut();

    // Log security event (non-blocking) - note: user already signed out so this may not work with RLS
    logSecurityEvent(currentUserId, 'logout', {});
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}